import React, { useState, useEffect, useCallback, useRef } from "react";
import { Redirect, useHistory } from "react-router-dom";
import Card from "@mui/material/Card";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import InboxIcon from "@mui/icons-material/MoveToInbox";
import SendIcon from "@mui/icons-material/Send";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiBadge from "components/VuiBadge";
import VuiButton from "components/VuiButton";
import LoadingSpinner from "components/LoadingSpinner";
import { useAuthz } from "context/AuthzContext";
import { useCompany } from "context/CompanyContext";
import { assignUnassignedEmail, deleteTenantInboxEmail, getAttachmentUrl, listTenantInbox, listClientProfiles, sendClientEmail, triggerEmailSync } from "services/tenantUserAdminService";
import { TC, FS, FW } from "../tokens/text";
import { db } from "../firebase";
import { collection, query, where, orderBy as fsOrderBy, limit, onSnapshot } from "firebase/firestore";

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const STATUS_STYLES = {
  sent: { bg: "rgba(0,117,255,0.12)", border: "rgba(0,117,255,0.35)", text: "#64b5f6", label: "Sent" },
  delivered: { bg: "rgba(76,175,80,0.10)", border: "rgba(76,175,80,0.3)", text: "#81c784", label: "Delivered" },
  opened: { bg: "rgba(0,230,118,0.10)", border: "rgba(0,230,118,0.3)", text: "#69f0ae", label: "Opened" },
  clicked: { bg: "rgba(0,230,118,0.15)", border: "rgba(0,230,118,0.4)", text: "#00e676", label: "Clicked" },
  bounced: { bg: "rgba(244,67,54,0.12)", border: "rgba(244,67,54,0.35)", text: "#ef9a9a", label: "Bounced" },
  failed: { bg: "rgba(244,67,54,0.12)", border: "rgba(244,67,54,0.35)", text: "#ef9a9a", label: "Failed" },
  spam: { bg: "rgba(255,152,0,0.10)", border: "rgba(255,152,0,0.3)", text: "#ffb74d", label: "Spam" },
  received: { bg: "rgba(156,39,176,0.10)", border: "rgba(156,39,176,0.3)", text: "#ce93d8", label: "Received" },
  read: { bg: "rgba(0,230,118,0.10)", border: "rgba(0,230,118,0.3)", text: "#69f0ae", label: "Read" },
  pending: { bg: "rgba(255,193,7,0.10)", border: "rgba(255,193,7,0.3)", text: "#ffd54f", label: "Pending" },
  unread: { bg: "rgba(144,202,249,0.10)", border: "rgba(144,202,249,0.3)", text: "#90caf9", label: "Unread" },
};

const DIRECTION_STYLES = {
  inbound: { bg: "rgba(156,39,176,0.10)", border: "rgba(156,39,176,0.3)", text: "#ce93d8", label: "↙ In" },
  outbound: { bg: "rgba(0,117,255,0.10)", border: "rgba(0,117,255,0.3)", text: "#90caf9", label: "↗ Out" },
};

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatListDate(dateStr) {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "—";
  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  }
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function attachmentIcon(att) {
  if (!att) return { icon: AttachFileIcon, color: "#90caf9", bg: "rgba(144,202,249,0.12)" };
  if (att.contentType?.includes("pdf") || att.name?.endsWith(".pdf")) return { icon: PictureAsPdfOutlinedIcon, color: "#ef9a9a", bg: "rgba(239,154,154,0.14)" };
  if (att.contentType?.includes("image")) return { icon: ImageOutlinedIcon, color: "#81c784", bg: "rgba(129,199,132,0.14)" };
  if (att.contentType?.includes("spreadsheet") || att.name?.endsWith(".xlsx") || att.name?.endsWith(".csv")) {
    return { icon: TableChartOutlinedIcon, color: "#ffd54f", bg: "rgba(255,213,79,0.14)" };
  }
  if (att.contentType?.includes("word") || att.name?.endsWith(".docx") || att.name?.endsWith(".doc")) {
    return { icon: DescriptionOutlinedIcon, color: "#90caf9", bg: "rgba(144,202,249,0.12)" };
  }
  return { icon: AttachFileIcon, color: "#90caf9", bg: "rgba(144,202,249,0.12)" };
}

function fileSizeLabel(bytes) {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function getBodySnippet(email) {
  const source = email?.bodyPreview || email?.bodyText || "";
  return source.replace(/\s+/g, " ").trim();
}

// Attachment preview classification. Some senders' mail clients attach files
// with a generic/incorrect Content-Type (e.g. "application/octet-stream"), so
// fall back to the filename extension when the declared type is unrecognized.
function classifyAttachmentPreview(contentType, name) {
  const ct = (contentType || "").toLowerCase();
  const ext = (name || "").toLowerCase().match(/\.[a-z0-9]+$/)?.[0] || "";

  const isImage = ct.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(ext);
  if (isImage) return "image";

  const isPdf = ct === "application/pdf" || ext === ".pdf";
  if (isPdf) return "pdf";

  const isText = ct.startsWith("text/") || [".txt", ".csv", ".log"].includes(ext);
  if (isText) return "text";

  const isOffice = /msword|wordprocessingml|ms-excel|spreadsheetml|ms-powerpoint|presentationml/.test(ct)
    || [".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx"].includes(ext);
  if (isOffice) return "office";

  return "unknown";
}

function isUnreadEmail(email) {
  return email?.direction === "inbound" && (email?.status || "").toLowerCase() === "unread";
}

// Some senders' HTML-to-text conversion (e.g. table-based layouts used purely
// for spacing) leaves behind lines made up of only "|" characters. Strip those
// spacer artifacts and collapse the resulting blank-line runs.
function cleanEmailBodyText(text) {
  if (!text) return text;
  const lines = text.split("\n").map((line) => (/^[\s|]*$/.test(line) ? "" : line));
  const cleaned = [];
  let blankRun = 0;
  for (const line of lines) {
    if (line.trim() === "") {
      blankRun += 1;
      if (blankRun <= 2) cleaned.push("");
    } else {
      blankRun = 0;
      cleaned.push(line);
    }
  }
  return cleaned.join("\n").trim();
}

function EmailBody({ text }) {
  if (!text) return null;
  const lines = cleanEmailBodyText(text).split("\n");
  const segments = [];
  let current = { quoted: false, lines: [] };

  for (const line of lines) {
    const isQuoted = /^>+\s?/.test(line);
    if (isQuoted !== current.quoted) {
      if (current.lines.length) segments.push({ ...current });
      current = { quoted: isQuoted, lines: [] };
    }
    current.lines.push(isQuoted ? line.replace(/^>+\s?/, "") : line);
  }

  if (current.lines.length) segments.push(current);

  return (
    <>
      {segments.map((seg, i) =>
        seg.quoted ? (
          <VuiBox
            key={i}
            sx={{
              borderLeft: "3px solid #d8e6f7",
              pl: 1.5,
              my: 1,
              opacity: 0.7,
            }}
          >
            <VuiTypography sx={{ color: TC.secondary, fontSize: "0.78rem", lineHeight: 1.7, whiteSpace: "pre-wrap", fontFamily: "inherit" }}>
              {seg.lines.join("\n")}
            </VuiTypography>
          </VuiBox>
        ) : (
          <VuiTypography key={i} sx={{ color: TC.primary, fontSize: "0.82rem", lineHeight: 1.7, whiteSpace: "pre-wrap", fontFamily: "inherit" }}>
            {seg.lines.join("\n")}
          </VuiTypography>
        )
      )}
    </>
  );
}

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status?.toLowerCase()] || {
    bg: "rgba(0,87,216,0.05)",
    border: "rgba(0,87,216,0.15)",
    text: TC.secondary,
    label: status || "—",
  };

  return (
    <VuiBox
      display="inline-flex"
      alignItems="center"
      px={0.9}
      py={0.3}
      sx={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: "6px", whiteSpace: "nowrap" }}
    >
      <VuiTypography sx={{ color: s.text, fontSize: "0.67rem", fontWeight: "bold", lineHeight: 1 }}>
        {s.label}
      </VuiTypography>
    </VuiBox>
  );
}

function DirectionBadge({ direction }) {
  const d = DIRECTION_STYLES[direction?.toLowerCase()] || {
    bg: "rgba(0,87,216,0.04)",
    border: "rgba(0,87,216,0.12)",
    text: TC.secondary,
    label: direction || "—",
  };

  return (
    <VuiBox
      display="inline-flex"
      alignItems="center"
      px={0.9}
      py={0.3}
      sx={{ background: d.bg, border: `1px solid ${d.border}`, borderRadius: "6px", whiteSpace: "nowrap" }}
    >
      <VuiTypography sx={{ color: d.text, fontSize: "0.67rem", fontWeight: "bold", lineHeight: 1 }}>
        {d.label}
      </VuiTypography>
    </VuiBox>
  );
}

function AttachmentList({ attachments, onOpen, onDownload, loading }) {
  if (!attachments?.length) {
    return <VuiTypography sx={{ color: TC.tertiary, fontSize: "0.72rem" }}>—</VuiTypography>;
  }

  return (
    <VuiBox display="flex" flexDirection="column" gap={0.75}>
      {attachments.map((att, idx) => {
        const badge = attachmentIcon(att);
        const Icon = badge.icon;
        const hasStorage = Boolean(att.storagePath);

        return (
          <VuiBox
            key={idx}
            display="flex"
            alignItems="center"
            gap={1}
            sx={{
              px: 1.2,
              py: 0.9,
              borderRadius: "12px",
              background: "#f4f8ff",
              border: "1px solid #d8e6f7",
              opacity: hasStorage ? 1 : 0.6,
            }}
          >
            <VuiBox
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                width: 30,
                height: 30,
                borderRadius: "9px",
                background: badge.bg,
                color: badge.color,
                flexShrink: 0,
              }}
            >
              <Icon sx={{ fontSize: 16, color: badge.color }} />
            </VuiBox>

            <VuiBox minWidth={0} flex={1}>
              <VuiTypography sx={{ color: TC.primary, fontSize: "0.76rem", fontWeight: 600, lineHeight: 1.3 }}>
                <span style={{ display: "inline-block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {att.name || "attachment"}
                </span>
              </VuiTypography>
              <VuiBox display="flex" alignItems="center" gap={0.8} flexWrap="wrap">
                {att.size > 0 && (
                  <VuiTypography sx={{ color: TC.secondary, fontSize: "0.64rem" }}>
                    {fileSizeLabel(att.size)}
                  </VuiTypography>
                )}
                {!hasStorage && (
                  <Tooltip title={att.uploadError || "This attachment was not saved to storage."}>
                    <VuiTypography sx={{ color: TC.tertiary, fontSize: "0.64rem", cursor: "help", textDecoration: "underline dotted" }}>
                      Preview unavailable{att.uploadError ? " — why?" : ""}
                    </VuiTypography>
                  </Tooltip>
                )}
              </VuiBox>
            </VuiBox>

            {hasStorage && (
              <VuiBox display="flex" gap={0.3} flexShrink={0}>
                <Tooltip title="Preview">
                  <span>
                    <IconButton
                      size="small"
                      aria-label={`Preview attachment ${att.name || "attachment"}`}
                      onClick={() => onOpen?.(att, idx)}
                      sx={{ color: TC.link, p: 0.5, "&:hover": { background: "rgba(0,117,255,0.08)", color: TC.primary } }}
                    >
                      <VisibilityIcon aria-hidden="true" sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Download">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => onDownload?.(att, idx)}
                      disabled={loading}
                      sx={{ color: TC.link, p: 0.5, "&:hover": { background: "rgba(0,117,255,0.08)", color: TC.primary } }}
                    >
                      <DownloadIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </VuiBox>
            )}
          </VuiBox>
        );
      })}
    </VuiBox>
  );
}

function FolderNavItem({ active, icon, label, badgeContent, onClick }) {
  return (
    <VuiBox
      onClick={onClick}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      gap={1}
      sx={{
        cursor: "pointer",
        borderRadius: "999px",
        px: 1.4,
        py: 1.1,
        background: active ? "rgba(0,117,255,0.12)" : "transparent",
        border: `1px solid ${active ? "rgba(0,117,255,0.28)" : "transparent"}`,
        transition: "all 0.2s ease",
        "&:hover": { background: active ? "rgba(0,117,255,0.14)" : "rgba(0,117,255,0.05)" },
      }}
    >
      <VuiBox display="flex" alignItems="center" gap={1.1} minWidth={0}>
        <VuiBox
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            background: active ? "rgba(0,117,255,0.16)" : "rgba(0,87,216,0.05)",
            color: active ? TC.link : TC.secondary,
          }}
        >
          {icon}
        </VuiBox>
        <VuiTypography sx={{ color: active ? TC.primary : TC.secondary, fontSize: "0.82rem", fontWeight: active ? 700 : 600 }}>
          {label}
        </VuiTypography>
      </VuiBox>
      <VuiBadge
        badgeContent={badgeContent}
        color="info"
        size="xs"
        container
        sx={{
          "& .MuiBadge-badge": {
            fontWeight: 900,
            fontSize: "0.68rem",
            background: active ? "#0075ff" : "rgba(0,117,255,0.12)",
            border: `1px solid ${active ? "rgba(0,117,255,0.55)" : "rgba(0,117,255,0.28)"}`,
            color: active ? "#ffffff" : "#0075ff",
            borderRadius: "999px",
            boxShadow: "none",
            minWidth: 24,
            height: 20,
            px: 0.75,
          },
        }}
      />
    </VuiBox>
  );
}

export default function EmailInboxPage() {
  const { role, loading: authLoading } = useAuthz();
  const { selectedCompany } = useCompany();
  const history = useHistory();

  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [orderBy, setOrderBy] = useState("date");
  const [orderDir, setOrderDir] = useState("desc");
  const [deletingId, setDeletingId] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [detailAttLoading, setDetailAttLoading] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeTo, setComposeTo] = useState("");
  const [composeSubject, setComposeSubject] = useState("");
  const [composeBody, setComposeBody] = useState("");
  const [composeSending, setComposeSending] = useState(false);
  const [composeMsg, setComposeMsg] = useState("");
  const [clientProfiles, setClientProfiles] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignEmail, setAssignEmail] = useState(null);
  const [assignClientId, setAssignClientId] = useState("");
  const [assignRemember, setAssignRemember] = useState(true);
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignError, setAssignError] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");

  const tenantId = selectedCompany?.id;

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const { emails: data } = await listTenantInbox(tenantId, { limit: 500 });
      setEmails(data);
    } catch (err) {
      setError(err?.message || "Failed to load inbox.");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  const handleSync = useCallback(async () => {
    if (!tenantId || syncing) return;
    setSyncing(true);
    setSyncMsg("");
    try {
      const result = await triggerEmailSync(tenantId);
      const fetched = result?.fetched ?? 0;
      setSyncMsg(fetched > 0 ? `✓ ${fetched} new email${fetched !== 1 ? "s" : ""} pulled` : "✓ Up to date");
      await load();
    } catch (err) {
      setSyncMsg(err?.message || "Sync failed.");
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncMsg(""), 4000);
    }
  }, [tenantId, syncing, load]);

  useEffect(() => {
    if (!tenantId) return;
    listClientProfiles({ tenantId })
      .then(({ clientProfiles: data }) => setClientProfiles(data))
      .catch(() => setClientProfiles([]));
  }, [tenantId]);

  const initializedRef = useRef(false);
  useEffect(() => {
    if (!tenantId) return;
    load();
    initializedRef.current = false;

    const q = query(
      collection(db, "companies", tenantId, "notifications"),
      where("type", "==", "inbound_email"),
      fsOrderBy("createdAt", "desc"),
      limit(1)
    );

    const unsubscribe = onSnapshot(q, () => {
      if (!initializedRef.current) {
        initializedRef.current = true;
        return;
      }
      load();
    });

    return () => unsubscribe();
  }, [tenantId, load]);

  useEffect(() => {
    if (!selectedEmail?.id) return;
    const updated = emails.find((item) => item.id === selectedEmail.id);
    if (!updated) {
      setSelectedEmail(null);
      return;
    }
    if (updated !== selectedEmail) setSelectedEmail(updated);
  }, [emails, selectedEmail]);

  if (authLoading) return null;
  if (!role || role === "none") return <Redirect to="/authentication/sign-in" />;

  const q = search.trim().toLowerCase();
  const filtered = emails.filter((e) => {
    if (!q) return true;
    return (
      (e.subject || "").toLowerCase().includes(q) ||
      (e.from || "").toLowerCase().includes(q) ||
      (e.to || "").toLowerCase().includes(q) ||
      (e.clientName || "").toLowerCase().includes(q) ||
      (e.status || "").toLowerCase().includes(q) ||
      (e.attachments || []).some((a) => (a.name || "").toLowerCase().includes(q))
    );
  });

  const inboxEmails = filtered.filter((e) => e.direction === "inbound");
  const sentEmails = filtered.filter((e) => e.direction === "outbound");
  const tabEmails = tab === 0 ? inboxEmails : sentEmails;

  const inboxCount = emails.filter((e) => e.direction === "inbound").length;
  const unreadInboxCount = emails.filter((e) => e.direction === "inbound" && (e.status || "").toLowerCase() === "unread").length;
  const sentCount = emails.filter((e) => e.direction === "outbound").length;

  const sorted = [...tabEmails].sort((a, b) => {
    let aVal;
    let bVal;

    if (orderBy === "date") {
      aVal = a.date ? new Date(a.date).getTime() : 0;
      bVal = b.date ? new Date(b.date).getTime() : 0;
    } else if (orderBy === "subject") {
      aVal = (a.subject || "").toLowerCase();
      bVal = (b.subject || "").toLowerCase();
    } else if (orderBy === "status") {
      aVal = (a.status || "").toLowerCase();
      bVal = (b.status || "").toLowerCase();
    } else if (orderBy === "client") {
      aVal = (a.clientName || a.from || a.to || "").toLowerCase();
      bVal = (b.clientName || b.from || b.to || "").toLowerCase();
    } else {
      aVal = a[orderBy] ?? "";
      bVal = b[orderBy] ?? "";
    }

    if (aVal < bVal) return orderDir === "asc" ? -1 : 1;
    if (aVal > bVal) return orderDir === "asc" ? 1 : -1;
    return 0;
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = sorted.slice(safePage * pageSize, safePage * pageSize + pageSize);

  const handleSort = (col) => {
    if (orderBy === col) {
      setOrderDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(col);
      setOrderDir(col === "date" ? "desc" : "asc");
    }
    setPage(0);
  };

  const handleSelectTab = (nextTab) => {
    setTab(nextTab);
    setPage(0);
    setSelectedEmail(null);
  };

  const requestDeleteEmail = (email) => {
    if (!tenantId || !email) return;
    setEmailToDelete(email);
    setDeleteConfirmOpen(true);
  };

  const openAssignDialog = (email) => {
    if (!email) return;
    setAssignEmail(email);
    setAssignClientId("");
    setAssignRemember(true);
    setAssignError("");
    setAssignOpen(true);
  };

  const closeAssignDialog = () => {
    if (assignSaving) return;
    setAssignOpen(false);
    setAssignEmail(null);
  };

  const confirmAssignEmail = async () => {
    if (!tenantId || !assignEmail || !assignClientId) return;
    setAssignSaving(true);
    setAssignError("");
    try {
      await assignUnassignedEmail(tenantId, assignEmail.id, assignClientId, assignRemember);
      setAssignOpen(false);
      setAssignEmail(null);
      setSelectedEmail(null);
      await load();
    } catch (err) {
      setAssignError(err?.message || "Failed to assign email.");
    } finally {
      setAssignSaving(false);
    }
  };

  const cancelDeleteEmail = () => {
    if (deletingId) return;
    setDeleteConfirmOpen(false);
    setEmailToDelete(null);
  };

  const confirmDeleteEmail = async () => {
    const email = emailToDelete;
    if (!tenantId || !email) return;
    setDeletingId(email.id);
    try {
      await deleteTenantInboxEmail(tenantId, email);
      setEmails((current) => current.filter((item) => item.id !== email.id));
      setSelectedEmail((current) => (current?.id === email.id ? null : current));
      setDeleteConfirmOpen(false);
      setEmailToDelete(null);
    } catch (err) {
      setError(err?.message || "Failed to delete email.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleOpenAttachment = async (email, attachment, index) => {
    if (!attachment?.storagePath) {
      setError("This attachment is not available for preview.");
      return;
    }
    setDetailAttLoading(true);
    try {
      const { url, name, contentType } = await getAttachmentUrl(tenantId, email.clientId, email.id, index, "preview");
      setAttachmentPreview({
        url,
        name: name || attachment?.name || "attachment",
        contentType: contentType || attachment?.contentType || "",
      });
    } catch (err) {
      setError(err?.message || "Failed to open attachment.");
    } finally {
      setDetailAttLoading(false);
    }
  };

  const openCompose = () => {
    setComposeOpen(true);
    setComposeMsg("");
  };

  const closeCompose = () => {
    setComposeOpen(false);
    setComposeTo("");
    setComposeSubject("");
    setComposeBody("");
    setComposeMsg("");
  };

  const handleReplyCompose = (email) => {
    openCompose();
    const toAddr = email?.direction === "inbound" ? email?.from || "" : email?.to || "";
    const subject = email?.subject || "";
    setComposeTo(toAddr);
    setComposeSubject(subject && !/^re:/i.test(subject) ? `Re: ${subject}` : subject);
  };

  const handleSendCompose = async () => {
    const toAddr = composeTo.trim();
    if (!toAddr) {
      setComposeMsg("Please enter a recipient email.");
      return;
    }
    if (!composeSubject.trim()) {
      setComposeMsg("Subject is required.");
      return;
    }
    if (!composeBody.trim()) {
      setComposeMsg("Message body is required.");
      return;
    }

    const extractEmail = (raw) => {
      const s = (raw || "").toLowerCase();
      const m = s.match(/<([^>]+)>/);
      return (m ? m[1] : s).trim();
    };
    const target = toAddr.toLowerCase();

    const matchedClient = clientProfiles.find((c) =>
      [c.contactEmail, c.secondaryContactEmail, c.email]
        .filter(Boolean)
        .some((addr) => addr.toLowerCase().trim() === target)
    );

    let clientId = matchedClient?.id || matchedClient?._id;

    if (!clientId) {
      const match = emails.find(
        (e) => extractEmail(e.to) === target || extractEmail(e.from) === target
      );
      clientId = match?.clientId;
    }

    if (!clientId) {
      setComposeMsg("No client found for this email address. Use the client profile page to send.");
      return;
    }

    setComposeSending(true);
    setComposeMsg("");
    try {
      await sendClientEmail(tenantId, clientId, {
        subject: composeSubject.trim(),
        bodyText: composeBody.trim(),
        toEmail: toAddr,
      });
      setComposeMsg("✓ Email sent successfully");
      setTimeout(closeCompose, 1200);
      load();
    } catch (err) {
      setComposeMsg(err?.message || "Failed to send email.");
    } finally {
      setComposeSending(false);
    }
  };

  const handleDownloadAttachment = async (email, attachment, index) => {
    if (!attachment?.storagePath) return;
    setDetailAttLoading(true);
    try {
      const { url } = await getAttachmentUrl(tenantId, email.clientId, email.id, index, "download");
      const win = window.open(url, "_blank");
      if (!win) window.location.href = url;
    } catch (err) {
      setError(err?.message || "Failed to download attachment.");
    } finally {
      setDetailAttLoading(false);
    }
  };

  const sortOptions = [
    { key: "date", label: "Date" },
    { key: "subject", label: "Subject" },
    { key: "client", label: tab === 0 ? "From" : "To" },
    { key: "status", label: "Status" },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <VuiBox mb={2}>
          <VuiTypography variant="h5" sx={{ color: TC.primary, fontWeight: FW.bold }}>
            Emails
          </VuiTypography>
        </VuiBox>
        <Card sx={{ background: "#ffffff", border: "1px solid #d8e6f7", borderRadius: "16px", overflow: "hidden" }}>
          {error && (
            <VuiBox px={3} pt={3}>
              <VuiBox px={2} py={1.2} sx={{ background: "rgba(244,67,54,0.1)", border: "1px solid rgba(244,67,54,0.3)", borderRadius: "10px" }}>
                <VuiTypography sx={{ color: "#ef9a9a", fontSize: "0.8rem" }}>{error}</VuiTypography>
              </VuiBox>
            </VuiBox>
          )}

          <VuiBox
            sx={{
              display: "flex",
              flexDirection: { xs: "column", lg: "row" },
              minHeight: { xs: "auto", lg: "calc(100vh - 220px)" },
              maxHeight: { xs: "none", lg: "calc(100vh - 220px)" },
            }}
          >
            <VuiBox
              sx={{
                width: { xs: "100%", lg: 240 },
                borderRight: { lg: "1px solid #e4eef8" },
                borderBottom: { xs: "1px solid #e4eef8", lg: "none" },
                background: "linear-gradient(180deg, #fbfdff 0%, #f7fbff 100%)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <VuiBox p={2.5} display="flex" flexDirection="column" gap={1}>
                <VuiButton
                  fullWidth
                  color="info"
                  variant="contained"
                  onClick={openCompose}
                  startIcon={<EditIcon sx={{ fontSize: "14px !important" }} />}
                  sx={{ borderRadius: "999px", py: 1, fontSize: "0.78rem", boxShadow: "none" }}
                >
                  Compose
                </VuiButton>
                <VuiButton
                  fullWidth
                  color="info"
                  variant="outlined"
                  onClick={handleSync}
                  disabled={syncing}
                  startIcon={syncing ? <CircularProgress size={12} color="inherit" /> : <RefreshIcon sx={{ fontSize: "14px !important" }} />}
                  sx={{ borderRadius: "999px", py: 1, fontSize: "0.78rem", boxShadow: "none" }}
                >
                  {syncing ? "Syncing…" : "Sync Now"}
                </VuiButton>
                {syncMsg && (
                  <VuiTypography sx={{ fontSize: "0.7rem", textAlign: "center", color: syncMsg.startsWith("✓") ? TC.success || "#4caf50" : TC.error }}>
                    {syncMsg}
                  </VuiTypography>
                )}
              </VuiBox>

              <VuiBox px={1.75} pb={2} display="flex" flexDirection="column" gap={0.8}>
                <FolderNavItem
                  active={tab === 0}
                  icon={<InboxIcon sx={{ fontSize: 18 }} />}
                  label="Inbox"
                  badgeContent={`${unreadInboxCount}/${inboxCount}`}
                  onClick={() => handleSelectTab(0)}
                />
                <FolderNavItem
                  active={tab === 1}
                  icon={<SendIcon sx={{ fontSize: 17 }} />}
                  label="Sent"
                  badgeContent={sentCount}
                  onClick={() => handleSelectTab(1)}
                />
              </VuiBox>

              <VuiBox px={2.5} pb={2.5} mt="auto">
                <VuiBox p={1.5} sx={{ background: "rgba(0,87,216,0.04)", border: "1px solid #d8e6f7", borderRadius: "14px" }}>
                  <VuiTypography sx={{ color: TC.secondary, fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.5 }}>
                    Mailbox
                  </VuiTypography>
                  <VuiTypography sx={{ color: TC.primary, fontSize: "0.86rem", fontWeight: 700 }}>
                    {loading ? "Refreshing…" : `${tabEmails.length} visible`}
                  </VuiTypography>
                  <VuiTypography sx={{ color: TC.secondary, fontSize: "0.72rem", mt: 0.35 }}>
                    {q ? "Search filter applied" : tab === 0 ? "Incoming conversations" : "Outgoing conversations"}
                  </VuiTypography>
                </VuiBox>
              </VuiBox>
            </VuiBox>

            <VuiBox
              sx={{
                width: { xs: "100%", lg: 390 },
                borderRight: { lg: "1px solid #e4eef8" },
                borderBottom: { xs: "1px solid #e4eef8", lg: "none" },
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                minHeight: 0,
                background: "#fcfeff",
              }}
            >
              <VuiBox p={2.25} sx={{ borderBottom: "1px solid #e4eef8" }}>
                <VuiBox display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={1.5}>
                  <VuiBox>
                    <VuiTypography sx={{ color: TC.primary, fontSize: "1rem", fontWeight: 700 }}>
                      {tab === 0 ? "Inbox" : "Sent"}
                    </VuiTypography>
                    <VuiTypography sx={{ color: TC.secondary, fontSize: "0.72rem" }}>
                      {loading ? "Loading messages…" : `${sorted.length} result${sorted.length !== 1 ? "s" : ""}${q ? " (filtered)" : ""}`}
                    </VuiTypography>
                  </VuiBox>
                  <Tooltip title="Refresh">
                    <span>
                      <IconButton aria-label="Refresh inbox" onClick={load} disabled={loading} sx={{ color: TC.secondary, "&:hover": { color: TC.primary, background: "rgba(0,117,255,0.06)" } }}>
                        {loading ? <CircularProgress size={18} sx={{ color: TC.link }} /> : <RefreshIcon aria-hidden="true" fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </VuiBox>

                <TextField
                  size="small"
                  fullWidth
                  placeholder={tab === 0 ? "Search from, subject…" : "Search to, subject…"}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(0);
                    setSelectedEmail(null);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: TC.secondary, fontSize: 16 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 1.4,
                    "& .MuiInputBase-root": { color: TC.primary, background: "#f4f8ff", borderRadius: "12px", fontSize: "0.8rem" },
                    "& .MuiInputBase-input": { py: "9px", px: "10px" },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d8e6f7" },
                    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#7a94b0" },
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#0075ff" },
                  }}
                />

                <VuiBox display="flex" flexWrap="wrap" gap={0.75}>
                  {sortOptions.map((option) => {
                    const active = orderBy === option.key;
                    return (
                      <VuiBox
                        key={option.key}
                        onClick={() => handleSort(option.key)}
                        display="inline-flex"
                        alignItems="center"
                        gap={0.45}
                        px={1}
                        py={0.55}
                        sx={{
                          cursor: "pointer",
                          borderRadius: "999px",
                          border: `1px solid ${active ? "rgba(0,117,255,0.32)" : "#d8e6f7"}`,
                          background: active ? "rgba(0,117,255,0.08)" : "transparent",
                          "&:hover": { background: "rgba(0,117,255,0.05)" },
                        }}
                      >
                        <UnfoldMoreIcon sx={{ fontSize: 14, color: active ? TC.link : TC.tertiary }} />
                        <VuiTypography sx={{ color: active ? TC.link : TC.secondary, fontSize: "0.68rem", fontWeight: 700 }}>
                          {option.label}
                        </VuiTypography>
                        {active && (
                          <VuiTypography sx={{ color: TC.link, fontSize: "0.64rem", fontWeight: 700 }}>
                            {orderDir === "asc" ? "↑" : "↓"}
                          </VuiTypography>
                        )}
                      </VuiBox>
                    );
                  })}
                </VuiBox>
              </VuiBox>

              <VuiBox sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
                {loading && !emails.length ? (
                  <VuiBox minHeight={280} display="flex" alignItems="center" justifyContent="center" px={3}>
                    <LoadingSpinner label="Loading emails…" />
                  </VuiBox>
                ) : pageRows.length === 0 ? (
                  <VuiBox minHeight={280} display="flex" alignItems="center" justifyContent="center" px={3} textAlign="center">
                    <VuiBox>
                      <InboxIcon sx={{ fontSize: 32, color: "#bdd2eb", mb: 1 }} />
                      <VuiTypography sx={{ color: TC.primary, fontSize: "0.88rem", fontWeight: 600 }}>
                        {q ? "No emails match your search." : tab === 0 ? "No received emails yet." : "No sent emails yet."}
                      </VuiTypography>
                    </VuiBox>
                  </VuiBox>
                ) : (
                  pageRows.map((email, idx) => {
                    const active = selectedEmail?.id === email.id;
                    const unread = isUnreadEmail(email);
                    const snippet = getBodySnippet(email);

                    return (
                      <VuiBox
                        key={email.id || idx}
                        onClick={() => setSelectedEmail(email)}
                        sx={{
                          px: 2.25,
                          py: 1.6,
                          cursor: "pointer",
                          background: active ? "rgba(0,117,255,0.10)" : "transparent",
                          borderLeft: active ? "3px solid #0075ff" : "3px solid transparent",
                          borderBottom: "1px solid #edf3fb",
                          transition: "background 0.2s ease",
                          "&:hover": { background: active ? "rgba(0,117,255,0.12)" : "#f6faff" },
                        }}
                      >
                        <VuiBox display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
                          <VuiBox minWidth={0} flex={1}>
                            <VuiTypography sx={{ color: TC.primary, fontSize: "0.8rem", fontWeight: unread ? 700 : 600, lineHeight: 1.25 }}>
                              <span style={{ display: "inline-block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {email.direction === "inbound" ? email.from || "—" : email.to || "—"}
                              </span>
                            </VuiTypography>
                          </VuiBox>
                          <VuiTypography sx={{ color: active ? TC.link : TC.secondary, fontSize: "0.68rem", whiteSpace: "nowrap", fontWeight: unread ? 700 : 500 }}>
                            {formatListDate(email.date)}
                          </VuiTypography>
                        </VuiBox>

                        <VuiTypography sx={{ color: TC.primary, fontSize: "0.8rem", fontWeight: unread ? 700 : 500, mt: 0.45 }}>
                          <span style={{ display: "inline-block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {email.subject || "(no subject)"}
                          </span>
                        </VuiTypography>

                        <VuiTypography sx={{ color: TC.secondary, fontSize: "0.72rem", mt: 0.35, lineHeight: 1.5 }}>
                          <span style={{ display: "inline-block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {snippet || "No preview available."}
                          </span>
                        </VuiTypography>

                        <VuiBox display="flex" alignItems="center" justifyContent="space-between" gap={1} mt={1}>
                          <VuiBox display="flex" alignItems="center" gap={0.8} minWidth={0}>
                            <VuiBox sx={{ width: 8, height: 8, borderRadius: "50%", background: unread ? "#0075ff" : STATUS_STYLES[(email.status || "").toLowerCase()]?.text || "#8aa4c0" }} />
                            <DirectionBadge direction={email.direction} />
                            {email.attachments?.length > 0 && (
                              <VuiBox display="inline-flex" alignItems="center" gap={0.35}>
                                <AttachFileIcon sx={{ fontSize: 13, color: TC.tertiary }} />
                                <VuiTypography sx={{ color: TC.tertiary, fontSize: "0.64rem", fontWeight: 700 }}>
                                  {email.attachments.length}
                                </VuiTypography>
                              </VuiBox>
                            )}
                          </VuiBox>
                          <StatusBadge status={email.mailjetStatus || email.status} />
                        </VuiBox>
                      </VuiBox>
                    );
                  })
                )}
              </VuiBox>

              <VuiBox px={2.25} py={1.5} sx={{ borderTop: "1px solid #e4eef8", background: "#ffffff" }}>
                <VuiBox display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
                  <VuiBox display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                    <VuiTypography sx={{ color: TC.secondary, fontSize: "0.72rem" }}>Rows:</VuiTypography>
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <VuiBox
                        key={n}
                        onClick={() => {
                          setPageSize(n);
                          setPage(0);
                        }}
                        px={0.9}
                        py={0.35}
                        sx={{
                          cursor: "pointer",
                          borderRadius: "999px",
                          border: `1px solid ${pageSize === n ? "rgba(0,117,255,0.32)" : "#d8e6f7"}`,
                          background: pageSize === n ? "rgba(0,117,255,0.08)" : "transparent",
                        }}
                      >
                        <VuiTypography sx={{ color: pageSize === n ? TC.link : TC.secondary, fontSize: "0.68rem", fontWeight: 700 }}>
                          {n}
                        </VuiTypography>
                      </VuiBox>
                    ))}
                  </VuiBox>

                  <VuiTypography sx={{ color: TC.secondary, fontSize: "0.72rem" }}>
                    {sorted.length === 0 ? "0 results" : `${safePage * pageSize + 1}–${Math.min(safePage * pageSize + pageSize, sorted.length)} of ${sorted.length}`}
                  </VuiTypography>
                </VuiBox>

                <VuiBox display="flex" alignItems="center" justifyContent="space-between" gap={1} mt={1}>
                  <IconButton
                    size="small"
                    disabled={safePage === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    sx={{ color: safePage === 0 ? TC.tertiary : TC.secondary, "&:hover": { color: TC.primary, background: "rgba(0,117,255,0.06)" } }}
                  >
                    <KeyboardArrowLeftIcon fontSize="small" />
                  </IconButton>

                  <VuiBox display="flex" alignItems="center" gap={0.55} flexWrap="wrap" justifyContent="center">
                    {Array.from({ length: totalPages }, (_, i) => i)
                      .filter((i) => i === 0 || i === totalPages - 1 || Math.abs(i - safePage) <= 1)
                      .reduce((acc, cur, idx, arr) => {
                        if (idx > 0 && cur - arr[idx - 1] > 1) acc.push("...");
                        acc.push(cur);
                        return acc;
                      }, [])
                      .map((item, idx) =>
                        item === "..." ? (
                          <VuiTypography key={`ellipsis-${idx}`} sx={{ color: TC.secondary, fontSize: "0.72rem", px: 0.3 }}>
                            …
                          </VuiTypography>
                        ) : (
                          <VuiBox
                            key={item}
                            onClick={() => setPage(item)}
                            px={0.95}
                            py={0.35}
                            sx={{
                              cursor: "pointer",
                              borderRadius: "999px",
                              minWidth: 28,
                              textAlign: "center",
                              background: safePage === item ? "rgba(0,117,255,0.12)" : "transparent",
                              border: `1px solid ${safePage === item ? "rgba(0,117,255,0.38)" : "#d8e6f7"}`,
                            }}
                          >
                            <VuiTypography sx={{ color: safePage === item ? TC.link : TC.secondary, fontSize: "0.68rem", fontWeight: 700 }}>
                              {item + 1}
                            </VuiTypography>
                          </VuiBox>
                        )
                      )}
                  </VuiBox>

                  <IconButton
                    size="small"
                    disabled={safePage >= totalPages - 1}
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    sx={{ color: safePage >= totalPages - 1 ? TC.tertiary : TC.secondary, "&:hover": { color: TC.primary, background: "rgba(0,117,255,0.06)" } }}
                  >
                    <KeyboardArrowRightIcon fontSize="small" />
                  </IconButton>
                </VuiBox>
              </VuiBox>
            </VuiBox>

            <VuiBox sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0, background: "#ffffff" }}>
              {selectedEmail ? (
                <>
                  <VuiBox p={2.5} sx={{ borderBottom: "1px solid #e4eef8" }}>
                    <VuiBox display="flex" alignItems="flex-start" justifyContent="space-between" gap={2} flexWrap="wrap">
                      <VuiBox minWidth={0}>
                        <VuiTypography sx={{ color: TC.primary, fontSize: "1.2rem", fontWeight: 700, lineHeight: 1.3 }}>
                          {selectedEmail.subject || "(no subject)"}
                        </VuiTypography>
                        <VuiBox display="flex" flexWrap="wrap" gap={1} alignItems="center" mt={1}>
                          <VuiTypography sx={{ color: TC.secondary, fontSize: "0.76rem" }}>
                            From: {selectedEmail.from || "—"}
                          </VuiTypography>
                          <VuiTypography sx={{ color: TC.secondary, fontSize: "0.76rem" }}>
                            To: {selectedEmail.to || "—"}
                          </VuiTypography>
                          {selectedEmail.clientName && (
                            <VuiTypography sx={{ color: TC.secondary, fontSize: "0.76rem" }}>
                              Client: {selectedEmail.clientName}
                            </VuiTypography>
                          )}
                          <VuiTypography sx={{ color: TC.tertiary, fontSize: "0.74rem" }}>
                            {formatDate(selectedEmail.date)}
                          </VuiTypography>
                        </VuiBox>
                        <VuiBox display="flex" flexWrap="wrap" gap={0.8} mt={1.2}>
                          <DirectionBadge direction={selectedEmail.direction} />
                          <StatusBadge status={selectedEmail.status} />
                          {selectedEmail.mailjetStatus && selectedEmail.mailjetStatus !== selectedEmail.status && (
                            <StatusBadge status={selectedEmail.mailjetStatus} />
                          )}
                        </VuiBox>
                      </VuiBox>

                      <VuiBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <VuiButton
                          size="small"
                          variant="outlined"
                          color="info"
                          onClick={() => handleReplyCompose(selectedEmail)}
                          startIcon={<EditIcon sx={{ fontSize: "13px !important" }} />}
                          sx={{ fontSize: "0.72rem", py: 0.45 }}
                        >
                          Reply
                        </VuiButton>
                        {selectedEmail.unassigned && (
                          <VuiButton
                            size="small"
                            variant="contained"
                            color="info"
                            onClick={() => openAssignDialog(selectedEmail)}
                            sx={{ fontSize: "0.72rem", py: 0.45 }}
                          >
                            Assign to client
                          </VuiButton>
                        )}
                        {selectedEmail.clientId && (
                          <VuiButton
                            size="small"
                            variant="outlined"
                            color="info"
                            onClick={() => history.push(`/client-profiles/${selectedEmail.clientId}`, { tab: 4, inboxTab: selectedEmail.direction === "outbound" ? 1 : 0 })}
                            endIcon={<OpenInNewIcon sx={{ fontSize: "13px !important" }} />}
                            sx={{ fontSize: "0.72rem", py: 0.45 }}
                          >
                            View client email
                          </VuiButton>
                        )}
                        <Tooltip title="Delete email">
                          <span>
                            <IconButton
                              onClick={() => requestDeleteEmail(selectedEmail)}
                              disabled={deletingId === selectedEmail.id}
                              sx={{ color: "#ff5252", background: "rgba(255,82,82,0.08)", border: "1px solid rgba(255,82,82,0.25)", borderRadius: "10px" }}
                            >
                              {deletingId === selectedEmail.id ? <CircularProgress size={16} sx={{ color: "#ff5252" }} /> : <DeleteOutlineIcon sx={{ fontSize: 18 }} />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </VuiBox>
                    </VuiBox>
                  </VuiBox>

                  <VuiBox sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
                    <VuiBox p={2.5}>
                      <VuiBox p={2.25} sx={{ background: "#ffffff", border: "1px solid #d8e6f7", borderRadius: "14px", mb: 2.5 }}>
                        {selectedEmail.bodyText ? (
                          <EmailBody text={selectedEmail.bodyText} />
                        ) : selectedEmail.bodyPreview ? (
                          <VuiTypography sx={{ color: TC.secondary, fontSize: "0.82rem", lineHeight: 1.7 }}>
                            {selectedEmail.bodyPreview}
                          </VuiTypography>
                        ) : (
                          <VuiTypography sx={{ color: TC.tertiary, fontSize: "0.78rem" }}>No message body available.</VuiTypography>
                        )}
                      </VuiBox>

                      <VuiBox p={2.25} sx={{ background: "#fbfdff", border: "1px solid #d8e6f7", borderRadius: "14px" }}>
                        <VuiBox display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={1.5}>
                          <VuiTypography sx={{ color: TC.primary, fontSize: "0.85rem", fontWeight: 700 }}>
                            Attachments {selectedEmail.attachments?.length ? `(${selectedEmail.attachments.length})` : ""}
                          </VuiTypography>
                          {detailAttLoading && <CircularProgress size={16} sx={{ color: TC.link }} />}
                        </VuiBox>
                        <AttachmentList
                          attachments={selectedEmail.attachments}
                          loading={detailAttLoading}
                          onOpen={(att, idx) => handleOpenAttachment(selectedEmail, att, idx)}
                          onDownload={(att, idx) => handleDownloadAttachment(selectedEmail, att, idx)}
                        />
                      </VuiBox>
                    </VuiBox>
                  </VuiBox>
                </>
              ) : (
                <VuiBox flex={1} display="flex" alignItems="center" justifyContent="center" p={4} textAlign="center">
                  <VuiBox>
                    <VuiBox
                      mx="auto"
                      mb={1.5}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={{ width: 72, height: 72, borderRadius: "50%", background: "rgba(0,117,255,0.08)" }}
                    >
                      <InboxIcon sx={{ fontSize: 34, color: "#7ea9d9" }} />
                    </VuiBox>
                    <VuiTypography sx={{ color: TC.primary, fontSize: "1rem", fontWeight: 700, mb: 0.6 }}>
                      Select an email to read
                    </VuiTypography>
                    <VuiTypography sx={{ color: TC.secondary, fontSize: "0.78rem" }}>
                      Choose a message from the list to open it here.
                    </VuiTypography>
                  </VuiBox>
                </VuiBox>
              )}
            </VuiBox>
          </VuiBox>
        </Card>
      </VuiBox>

      <Dialog open={composeOpen} onClose={closeCompose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <VuiTypography sx={{ fontSize: "0.95rem", fontWeight: 700, color: TC.primary }}>
            ✉️ New Email
          </VuiTypography>
          <IconButton aria-label="Close compose dialog" size="small" onClick={closeCompose} sx={{ color: TC.secondary }}>
            <CloseIcon aria-hidden="true" fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <TextField
            label="To"
            size="small"
            fullWidth
            value={composeTo}
            onChange={(e) => setComposeTo(e.target.value)}
            placeholder="recipient@example.com"
            sx={{
              "& .MuiOutlinedInput-root": { color: TC.primary, fontSize: "0.82rem", background: "#f4f8ff", "& fieldset": { borderColor: "rgba(0,87,216,0.2)" }, "&:hover fieldset": { borderColor: "rgba(0,87,216,0.4)" }, "&.Mui-focused fieldset": { borderColor: "#0057d8" } },
              "& .MuiInputLabel-root": { color: TC.secondary, fontSize: "0.78rem" },
              "& .MuiInputLabel-root.Mui-focused": { color: TC.link },
            }}
          />
          <TextField
            label="Subject"
            size="small"
            fullWidth
            value={composeSubject}
            onChange={(e) => setComposeSubject(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": { color: TC.primary, fontSize: "0.82rem", background: "#f4f8ff", "& fieldset": { borderColor: "rgba(0,87,216,0.2)" }, "&:hover fieldset": { borderColor: "rgba(0,87,216,0.4)" }, "&.Mui-focused fieldset": { borderColor: "#0057d8" } },
              "& .MuiInputLabel-root": { color: TC.secondary, fontSize: "0.78rem" },
              "& .MuiInputLabel-root.Mui-focused": { color: TC.link },
            }}
          />
          <TextField
            label="Message"
            size="small"
            fullWidth
            multiline
            rows={7}
            value={composeBody}
            onChange={(e) => setComposeBody(e.target.value)}
            placeholder="Write your message here…"
            sx={{
              "& .MuiOutlinedInput-root": { color: TC.primary, fontSize: "0.82rem", background: "#f4f8ff", "& fieldset": { borderColor: "rgba(0,87,216,0.2)" }, "&:hover fieldset": { borderColor: "rgba(0,87,216,0.4)" }, "&.Mui-focused fieldset": { borderColor: "#0057d8" } },
              "& .MuiInputLabel-root": { color: TC.secondary, fontSize: "0.78rem" },
              "& .MuiInputLabel-root.Mui-focused": { color: TC.link },
            }}
          />
          {composeMsg && (
            <VuiTypography sx={{ fontSize: "0.76rem", color: composeMsg.startsWith("✓") ? TC.success : TC.error }}>
              {composeMsg}
            </VuiTypography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <VuiButton size="small" variant="outlined" color="text" onClick={closeCompose} sx={{ fontSize: "0.75rem" }}>
            Cancel
          </VuiButton>
          <VuiButton
            size="small"
            color="info"
            disabled={composeSending || !composeTo.trim() || !composeSubject.trim() || !composeBody.trim()}
            onClick={handleSendCompose}
            startIcon={composeSending ? <CircularProgress size={12} color="inherit" /> : <SendIcon sx={{ fontSize: "13px !important" }} />}
            sx={{ fontSize: "0.75rem" }}
          >
            {composeSending ? "Sending…" : "Send Email"}
          </VuiButton>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(attachmentPreview)} onClose={() => setAttachmentPreview(null)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>{attachmentPreview?.name || "Attachment preview"}</DialogTitle>
        <DialogContent dividers sx={{ p: 0, minHeight: "70vh", background: "#0a1628" }}>
          {(() => {
            const nm  = attachmentPreview?.name || "";
            const url = attachmentPreview?.url || "";
            if (!url) {
              return (
                <VuiBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ minHeight: "70vh", p: 4, textAlign: "center" }}>
                  <VuiTypography sx={{ color: "#fff", fontSize: "0.85rem" }}>Attachment URL not available.</VuiTypography>
                </VuiBox>
              );
            }
            const kind = classifyAttachmentPreview(attachmentPreview?.contentType, nm);
            if (kind === "image") {
              return <img src={url} alt={nm} style={{ width: "100%", display: "block" }} />;
            }
            if (kind === "pdf" || kind === "text") {
              return <iframe title={nm || "Attachment preview"} src={url} style={{ width: "100%", height: "75vh", border: 0 }} />;
            }
            if (kind === "office") {
              const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
              return <iframe title={nm || "Attachment preview"} src={viewerUrl} style={{ width: "100%", height: "75vh", border: 0, background: "#fff" }} />;
            }
            return (
              <VuiBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={2} sx={{ minHeight: "70vh", p: 4, textAlign: "center" }}>
                <VuiTypography sx={{ color: "#fff", fontSize: "0.85rem" }}>Preview not available for this file type.</VuiTypography>
                <VuiButton color="info" variant="outlined" onClick={() => { const w = window.open("", "_blank"); if (w) w.location.href = url; }}>
                  ⬇ Download {nm}
                </VuiButton>
              </VuiBox>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Link href={attachmentPreview?.url} target="_blank" rel="noopener noreferrer" sx={{ mr: "auto" }}>
            Open in new tab
          </Link>
          <VuiButton onClick={() => setAttachmentPreview(null)}>Close</VuiButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteConfirmOpen}
        onClose={cancelDeleteEmail}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundImage: "none",
            background: "#ffffff",
            border: "1px solid #d8e6f7",
            borderRadius: "16px",
            boxShadow: "0 8px 40px rgba(0,87,216,0.12), 0 2px 8px rgba(0,0,0,0.06)",
            color: TC.primary,
            minWidth: 360,
            maxWidth: 460,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 2.5, px: 3 }}>
          <VuiBox display="flex" alignItems="center" gap={1.5}>
            <VuiBox
              sx={{
                background: "rgba(245,124,0,0.08)",
                border: "1px solid rgba(245,124,0,0.25)",
                borderRadius: "10px",
                p: 0.8,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <WarningAmberRoundedIcon sx={{ color: "#f57c00", fontSize: "1.8rem" }} />
            </VuiBox>
            <VuiTypography variant="h5" fontWeight="bold" sx={{ color: TC.primary, letterSpacing: "0.01em" }}>
              Delete Email
            </VuiTypography>
          </VuiBox>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 1, px: 3 }}>
          <VuiBox
            sx={{
              background: "rgba(245,124,0,0.05)",
              border: "1px solid rgba(245,124,0,0.18)",
              borderRadius: "12px",
              px: 2.5,
              py: 2,
            }}
          >
            <VuiTypography variant="body2" sx={{ color: TC.primary, lineHeight: 1.7, fontSize: "0.95rem" }}>
              Are you sure you want to delete{" "}
              <strong style={{ color: TC.primary }}>{emailToDelete?.subject || "this email"}</strong>?
            </VuiTypography>
            <VuiTypography variant="body2" sx={{ color: TC.secondary, mt: 1, fontSize: "0.85rem" }}>
              This action cannot be undone.
            </VuiTypography>
          </VuiBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1.5, justifyContent: "flex-end" }}>
          <VuiButton
            variant="outlined"
            size="medium"
            onClick={cancelDeleteEmail}
            disabled={!!deletingId}
            sx={{
              borderColor: "rgba(74,96,128,0.4)",
              color: TC.secondary,
              fontWeight: 600,
              "&:hover": { borderColor: "#0a1628", background: "rgba(10,22,40,0.04)" },
            }}
          >
            Cancel
          </VuiButton>
          <VuiButton
            size="medium"
            onClick={confirmDeleteEmail}
            disabled={!!deletingId}
            startIcon={deletingId ? null : <DeleteOutlineIcon fontSize="small" />}
            sx={{
              background: "linear-gradient(135deg, #fb8c00, #e65100)",
              color: TC.white,
              fontWeight: 700,
              "&:hover": { background: "linear-gradient(135deg, #ffa726, #f57c00)" },
              boxShadow: "0 4px 18px rgba(251,140,0,0.35)",
            }}
          >
            {deletingId ? "Deleting…" : "Delete"}
          </VuiButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={assignOpen}
        onClose={closeAssignDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundImage: "none",
            background: "#ffffff",
            border: "1px solid #d8e6f7",
            borderRadius: "16px",
            boxShadow: "0 8px 40px rgba(0,87,216,0.12), 0 2px 8px rgba(0,0,0,0.06)",
            color: TC.primary,
            minWidth: 360,
            maxWidth: 460,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 2.5, px: 3 }}>
          <VuiTypography variant="h5" fontWeight="bold" sx={{ color: TC.primary }}>
            Assign email to client
          </VuiTypography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 1, px: 3 }}>
          <VuiTypography variant="body2" sx={{ color: TC.secondary, fontSize: "0.85rem", mb: 2 }}>
            From <strong style={{ color: TC.primary }}>{assignEmail?.from || "—"}</strong>
            {assignEmail?.subject ? ` — "${assignEmail.subject}"` : ""}
          </VuiTypography>

          <TextField
            select
            fullWidth
            size="small"
            label="Client"
            value={assignClientId}
            onChange={(e) => setAssignClientId(e.target.value)}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          >
            <option value="">Select a client…</option>
            {[...clientProfiles]
              .map((c) => ({
                id: c.id || c._id,
                name: c.legalName || c.tradingName || c.legal_identity?.legal_name || c.name || c.id || c._id,
              }))
              .filter((c) => c.id)
              .sort((a, b) => String(a.name).localeCompare(String(b.name)))
              .map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
          </TextField>

          <VuiBox display="flex" alignItems="flex-start" gap={1}>
            <input
              id="assign-remember-sender"
              type="checkbox"
              checked={assignRemember}
              onChange={(e) => setAssignRemember(e.target.checked)}
              style={{ marginTop: 3, cursor: "pointer" }}
            />
            <label htmlFor="assign-remember-sender" style={{ cursor: "pointer" }}>
              <VuiTypography variant="body2" sx={{ color: TC.primary, fontSize: "0.82rem" }}>
                Remember this sender
              </VuiTypography>
              <VuiTypography variant="body2" sx={{ color: TC.secondary, fontSize: "0.75rem" }}>
                Saves {assignEmail?.from || "the sender"} as the client&apos;s contact email so future
                messages are matched automatically.
              </VuiTypography>
            </label>
          </VuiBox>

          {assignError && (
            <VuiTypography variant="body2" sx={{ color: "#d32f2f", fontSize: "0.8rem", mt: 1.5 }}>
              {assignError}
            </VuiTypography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1.5, justifyContent: "flex-end" }}>
          <VuiButton
            variant="outlined"
            size="medium"
            onClick={closeAssignDialog}
            disabled={assignSaving}
            sx={{
              borderColor: "rgba(74,96,128,0.4)",
              color: TC.secondary,
              fontWeight: 600,
              "&:hover": { borderColor: "#0a1628", background: "rgba(10,22,40,0.04)" },
            }}
          >
            Cancel
          </VuiButton>
          <VuiButton
            size="medium"
            onClick={confirmAssignEmail}
            disabled={assignSaving || !assignClientId}
            sx={{
              background: "linear-gradient(135deg,#1565c0,#1976d2)",
              color: TC.white,
              fontWeight: 700,
              "&:hover": { background: "linear-gradient(135deg,#0d47a1,#1565c0)" },
            }}
          >
            {assignSaving ? "Assigning…" : "Assign"}
          </VuiButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

